/**
 * The debug channel: `debuglog('http-request')` hands back a function that
 * writes when its namespace is switched on, and does nothing at all when it is
 * not.
 *
 * It rides on the runtime's own switch rather than on a logger of ours, so
 * almost this whole file answers one question: which value of `NODE_DEBUG`
 * turns which namespace on. The answer is Node's answer, MEASURED against
 * `util.debuglog` and written down in DB7 through DB10 -- the quirk in DB10
 * included, where a single space after a comma switches an entry off. "It works
 * like NODE_DEBUG" is a stronger promise than "it works almost like
 * NODE_DEBUG", and whoever finds the first difference starts wondering where
 * else we drifted.
 */

/**
 * A debug channel. `enabled` is readable WITHOUT calling it, which is what lets
 * a call site keep an expensive argument unbuilt while the namespace is off
 * (DB11).
 */
export interface Debug {
  (format: string, ...args: unknown[]): void;
  readonly enabled: boolean;
}

/** Only what this file uses of `process`, so a browser-safe module names no Node type. */
interface ProcessLike {
  readonly env: Record<string, string | undefined>;
  readonly pid: number;
}

/** Only what this file uses of `localStorage`, for the same reason. */
interface StorageLike {
  getItem(key: string): string | null;
}

/**
 * The platform's own `process`, or `undefined` in a runtime that has none.
 *
 * A function with a declared return type rather than a plain read, for the same
 * reason `platformFetch` in `request.ts` is one: the ambient types declare
 * `globalThis.process` as always present, so a `const` annotated
 * `ProcessLike | undefined` is narrowed straight back to `ProcessLike` and the
 * absence becomes inexpressible without an assertion. A RETURN type is not
 * narrowed that way, so this is what keeps the honest half of the union alive.
 */
function platformProcess(): ProcessLike | undefined {
  return globalThis.process;
}

/** The same shape for `localStorage`, which the DOM lib likewise declares as always there. */
function platformStorage(): StorageLike | undefined {
  return globalThis.localStorage;
}

/**
 * The spec that decides, or `''` when nothing is set.
 *
 * DB5 makes this an ORDER rather than a runtime switch: `NODE_DEBUG` decides
 * whenever it is set at all, and `localStorage.debug` only gets a turn when it
 * is not. A real runtime has one of the two, never both, so the difference is
 * visible only in a test that arranges both -- and there "Node wins" is the
 * expectation.
 *
 * The `localStorage` read is guarded because in a browser privacy mode the
 * ACCESS itself throws (DB5). A namespace that stays off is the right outcome
 * there; a caller that breaks on a logging question is not.
 */
function readSpec(): string {
  const fromEnvironment = platformProcess()?.env.NODE_DEBUG;
  if (fromEnvironment !== undefined) return fromEnvironment;
  try {
    return platformStorage()?.getItem('debug') ?? '';
  } catch {
    return '';
  }
}

/**
 * The characters Node escapes before it compiles the spec. `*` is deliberately
 * absent: it is the one wildcard, and the step after this turns it into `.*`.
 */
const METACHARACTERS = /[|\\{}()[\]^$+?.]/g;

/** One term of the spec as an anchored pattern, escaped exactly as Node escapes it. */
function toPattern(term: string): string {
  const escaped = term
    .replaceAll(METACHARACTERS, String.raw`\$&`)
    .replaceAll('*', '.*');
  return `^${escaped}$`;
}

/**
 * Whether the spec switches this namespace on.
 *
 * Split on `,` and NOT trimmed, which is where DB10's quirk comes from: Node
 * splits the same way, so `'foo, http-request'` asks for a namespace whose name
 * opens with a space, and nothing is called that. Anchoring every term at BOTH
 * ends is what makes `http-request:*` skip the bare name while `http-request*`
 * keeps it, and what keeps a middle `*` from matching past the end (DB8). An
 * empty spec compiles to `^$`, which matches no real namespace -- the same
 * nothing that Node's own `/^$/` fallback matches.
 */
function matches(spec: string, namespace: string): boolean {
  const pattern = spec.split(',').map(toPattern).join('|');
  return new RegExp(pattern, 'i').test(namespace);
}

/** DB11: a real no-op. No format string is built, and the console is never reached. */
function noop(): void {}

/** One shared instance: a switched-off channel has no identity worth keeping apart. */
const silent: Debug = Object.assign(noop, { enabled: false });

/**
 * The two channels of DB6. Node writes to stderr, as `util.debuglog` does, so
 * debug output never lands in a stdout pipe. A browser has no such split, and
 * `console.error` there paints a red error marker on a line that is not an
 * error, so that side writes to `console.log`.
 *
 * Both go through `globalThis.console` rather than the bare binding. The read
 * happens per line, so a spy installed after a channel was built still sees it,
 * and the package's stray-logging gate -- which allows `warn` and `error` only
 * -- has nothing to say about the one module whose whole job is this write.
 */
function toStderr(format: string, ...args: unknown[]): void {
  globalThis.console.error(format, ...args);
}

function toStdout(format: string, ...args: unknown[]): void {
  globalThis.console.log(format, ...args);
}

/**
 * The channels resolved so far, and the spec they were resolved FROM.
 *
 * DB14. Every call site asks for its channel per CALL, so the regexp build in
 * `matches` sits on the hottest path this library has. What makes a memo safe
 * is that the spec is the channel's ONLY input: hold on to the string a table
 * was built from and a change is one string comparison away.
 *
 * So this caches the ANSWER, never the QUESTION. The spec is still read on
 * every call, and a spec that differs from the remembered one throws the whole
 * table away rather than serving a stale row -- which is what keeps a caller
 * who sets `NODE_DEBUG` between two calls getting an answer about the
 * environment they just set. Clearing the table WHOLESALE rather than
 * re-resolving row by row is the same rule stated once: no row may outlive the
 * spec that produced it.
 *
 * `undefined` rather than `''` as the starting value, because `''` is a real
 * spec (DB10: set but empty) and a starting value that collides with one would
 * skip the first clear.
 */
let resolvedFrom: string | undefined;
const resolved = new Map<string, Debug>();

/**
 * The channel for one namespace, from the spec that is in force right now.
 *
 * Everything is resolved HERE, on the call, and nothing when this module loads.
 * A caller that sets the environment and then asks for a channel gets an answer
 * about the environment it just set.
 */
export function debuglog(namespace: string): Debug {
  const spec = readSpec();
  if (spec !== resolvedFrom) {
    resolved.clear();
    resolvedFrom = spec;
  }
  const known = resolved.get(namespace);
  if (known !== undefined) return known;

  const channel = resolve(spec, namespace);
  resolved.set(namespace, channel);
  return channel;
}

/** The channel itself, built once per namespace per spec by `debuglog` above. */
function resolve(spec: string, namespace: string): Debug {
  if (!matches(spec, namespace)) return silent;

  const runtime = platformProcess();
  // DB13: in Node the process id rides along, as it does in `util.debuglog`. In
  // a browser it names nothing, so it is left out there.
  const prefix =
    runtime === undefined ? `${namespace}:` : `${namespace} ${runtime.pid}:`;
  const channel = runtime === undefined ? toStdout : toStderr;

  // DB13: the namespace goes INSIDE the format string. `console.*` treats only
  // its FIRST argument as a format string, so handing the prefix over
  // separately would silently swallow the caller's own `%o`.
  const write = (format: string, ...args: unknown[]): void => {
    channel(`${prefix} ${format}`, ...args);
  };
  return Object.assign(write, { enabled: true });
}
